import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaTrashAlt, FaEye } from "react-icons/fa";
import {
  FiSearch,
  FiRefreshCw,
  FiAlertCircle,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { baseurl } from "../../config/url";

const SaleBySeller = () => {
  const [interestedUsers, setInterestedUsers] = useState([]);
  const [selected, setSelected] = useState(null); // 🔥 modal state

  const [query, setQuery] = useState("");
  const [uiLoading, setUiLoading] = useState(true);
  const [uiError, setUiError] = useState("");

  const fetchInterestedUsers = async () => {
    setUiLoading(true);
    setUiError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${baseurl}/api/seller/interested-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInterestedUsers(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setUiError("Failed to load interested users.");
      setInterestedUsers([]);
    } finally {
      setUiLoading(false);
    }
  };

  useEffect(() => {
    fetchInterestedUsers();
  }, []);

  const safeList = useMemo(
    () => (Array.isArray(interestedUsers) ? interestedUsers : []),
    [interestedUsers]
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return safeList;

    return safeList.filter((e) =>
      [
        e?.userId?.name,
        e?.userId?.email,
        e?.userId?.mobile,
        e?.productId?.name,
        e?.productId?.saleType,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [safeList, query]);

  const handleRemoveUser = async (userId, productId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${baseurl}/api/seller/remove-interested-user`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { userId, productId },
        }
      );
      fetchInterestedUsers();
    } catch {}
  };

  // LOADING
  if (uiLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  // ERROR
  if (uiError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{uiError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative">
      {/* HEADER */}
      <div className="sticky top-0 bg-white/80 backdrop-blur border-b p-4 flex justify-between items-center">
        <h1 className="font-bold text-xl">Interested Users</h1>

        <div className="flex gap-3 items-center">
          <span className="bg-white border px-3 py-1 rounded-xl">
            {safeList.length}
          </span>

          <button
            onClick={fetchInterestedUsers}
            className="bg-black text-white px-4 py-2 rounded-xl flex items-center gap-2"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* SEARCH */}
      <div className="p-4">
        <div className="relative">
          <FiSearch className="absolute top-3 left-3 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 py-3 border rounded-xl"
          />
        </div>
      </div>

      {/* LIST */}
      <div className="p-4 grid gap-4">
        {filtered.map((entry, i) => {
          const product = entry?.productId;
          const user = entry?.userId;

          return (
            <div
              key={i}
              className="bg-white p-4 rounded-2xl shadow flex justify-between items-center"
            >
              <div className="flex gap-4 items-center">
                {product?.image?.data ? (
                  <img
                    src={`data:${product.image.contentType};base64,${product.image.data}`}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                    N/A
                  </div>
                )}

                <div>
                  <p className="font-bold">{product?.name}</p>
                  <p className="text-sm text-gray-500">{user?.name}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setSelected(entry)}
                  className="p-2 bg-blue-500 text-white rounded-lg"
                >
                  <FaEye />
                </button>

                <button
                  onClick={() =>
                    handleRemoveUser(user?._id, product?._id)
                  }
                  className="p-2 bg-red-500 text-white rounded-lg"
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* 🔥 MODAL CARD VIEW */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 relative shadow-xl animate-scaleIn">
            {/* CLOSE */}
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 text-gray-500"
            >
              <FiX size={20} />
            </button>

            {/* IMAGE */}
            {selected?.productId?.image?.data && (
              <img
                src={`data:${selected.productId.image.contentType};base64,${selected.productId.image.data}`}
                className="w-full h-48 object-cover rounded-xl mb-4"
              />
            )}

            {/* PRODUCT */}
            <h2 className="text-xl font-bold">
              {selected?.productId?.name}
            </h2>

            <p className="text-green-600 font-bold mt-1">
              ₹{selected?.productId?.price}
            </p>

            {/* USER INFO */}
            <div className="mt-4 border-t pt-4 space-y-2">
              <p>
                <span className="font-semibold">Name:</span>{" "}
                {selected?.userId?.name}
              </p>
              <p>
                <span className="font-semibold">Email:</span>{" "}
                {selected?.userId?.email}
              </p>
              <p>
                <span className="font-semibold">Mobile:</span>{" "}
                {selected?.userId?.mobile}
              </p>
              <p>
                <span className="font-semibold">Sale Type:</span>{" "}
                {selected?.productId?.saleType}
              </p>
            </div>

            {/* ACTION */}
            <button
              onClick={() => setSelected(null)}
              className="mt-6 w-full bg-black text-white py-2 rounded-xl"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleBySeller;